import type { StudySwimlaneSelectDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { type MeiaThreadMeasureRow, useGetMeiaThreadMeasureRollup } from '@queries/meia.query';
import { type FC, useMemo, useState } from 'react';

type MeiaThreadMeasuresTilesProps = {
  missionId: string;
  studyId: string;
  swimlanes: StudySwimlaneSelectDto[];
};

const VISIBLE_ROW_LIMIT = 4;

type MeasureTileProps = {
  isLoading: boolean;
  rows: MeiaThreadMeasureRow[];
  stageNameById: Map<string, string>;
  subtitle: string;
  title: string;
};

const MeasureTile: FC<MeasureTileProps> = (props) => {
  const { isLoading, rows, stageNameById, subtitle, title } = props;
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleRows = isExpanded ? rows : rows.slice(0, VISIBLE_ROW_LIMIT);
  const hiddenCount = rows.length - visibleRows.length;

  return (
    <Tile
      contentClassName="gap-2"
      header={{
        title: (
          <span className="flex w-full items-center justify-between gap-2">
            <span>
              {title} <span className="text-gray-400">— {subtitle}</span>
            </span>
            <span className="text-xs font-normal text-gray-400">{rows.length}</span>
          </span>
        ),
      }}
    >
      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">None authored on this Thread's Stages yet.</p>
      ) : (
        <ul className="flex flex-col">
          {visibleRows.map((row) => (
            <li
              className="flex items-baseline gap-3 border-b border-gray-100 py-1.5 last:border-b-0"
              key={row.id}
            >
              <span className="w-16 shrink-0 truncate text-[10px] font-semibold tracking-wide text-primary">
                {stageNameById.get(row.swimlaneId) ?? '—'}
              </span>
              <span className="text-xs leading-snug text-gray-700">{row.description}</span>
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="pt-1">
              <Button
                className="h-auto p-0 text-xs"
                onClick={() => setIsExpanded(true)}
                size="xs"
                type="button"
                variant="link"
              >
                + {hiddenCount} more
              </Button>
            </li>
          )}
        </ul>
      )}
    </Tile>
  );
};

/**
 * The three thread-level rollup tiles (MoS / MoE / MoP) shown under the Thread Stages tile in P2.
 * Each lists its measures collected across every Stage of the Thread, tagged with the Stage name.
 */
export const MeiaThreadMeasuresTiles: FC<MeiaThreadMeasuresTilesProps> = (props) => {
  const { missionId, studyId, swimlanes } = props;
  const { isLoading, moe, mop, mos } = useGetMeiaThreadMeasureRollup({ missionId, studyId, swimlanes });

  const stageNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const swimlane of swimlanes) {
      map.set(swimlane.id, swimlane.phaseName);
    }
    return map;
  }, [swimlanes]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <MeasureTile
        isLoading={isLoading}
        rows={mos}
        stageNameById={stageNameById}
        subtitle="Success"
        title="MoS"
      />
      <MeasureTile
        isLoading={isLoading}
        rows={moe}
        stageNameById={stageNameById}
        subtitle="Effectiveness"
        title="MoE"
      />
      <MeasureTile
        isLoading={isLoading}
        rows={mop}
        stageNameById={stageNameById}
        subtitle="Performance"
        title="MoP"
      />
    </div>
  );
};
